"use client"

import { useRef, type ReactNode } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { PageSectionHeader } from "./page-section-header"

/** Standard page width — matches the hub page layout container */
const CONTENT_W = "max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-8"

interface ScrollCarouselProps {
  /** Small uppercase eyebrow text */
  eyebrow: string
  /** Main headline */
  title: string
  /** Background color class (defaults to "bg-white") */
  bgClass?: string
  /** Carousel card children */
  children: ReactNode
  /** Additional classes on the outer section */
  className?: string
}

/**
 * ScrollCarousel — Full-width product carousel section used across hub pages.
 *
 * Renders a PageSectionHeader with left/right scroll buttons, followed by a
 * horizontal scrollable container. Background color alternates between
 * bg-white and bg-gray-50 to create visual rhythm between sections.
 */
export function ScrollCarousel({ eyebrow, title, bgClass = "bg-white", children, className = "" }: ScrollCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const scroll = (dir: "left" | "right") => {
    scrollRef.current?.scrollBy({ left: dir === "left" ? -300 : 300, behavior: "smooth" })
  }

  return (
    <section className={`${bgClass} py-12 ${className}`}>
      <div className={CONTENT_W}>
        <PageSectionHeader
          eyebrow={eyebrow}
          title={title}
          actions={
            <>
              {/* FRIDGE-149 §1 — named prev/next (bare chevrons otherwise
                  announce as "button"). Chevron colour: FRIDGE-142, owner. */}
              <button
                type="button"
                onClick={() => scroll("left")}
                aria-label="Scroll left"
                className="w-10 h-10 flex items-center justify-center rounded-lg bg-white hover:bg-gray-100 transition-colors shadow-sm border border-gray-100"
              >
                <ChevronLeft className="w-5 h-5 text-[#ff9951]" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => scroll("right")}
                aria-label="Scroll right"
                className="w-10 h-10 flex items-center justify-center rounded-lg bg-white hover:bg-gray-100 transition-colors shadow-sm border border-gray-100"
              >
                <ChevronRight className="w-5 h-5 text-[#ff9951]" aria-hidden="true" />
              </button>
            </>
          }
        />
        <div
          ref={scrollRef}
          className="flex overflow-x-auto gap-6 snap-x snap-mandatory pb-4"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
        >
          {children}
        </div>
      </div>
    </section>
  )
}
